import { useEffect, useState } from 'react'
import { ownedPromptsApi } from '../../api/owned-prompts'
import { LegacyProjectPromptsTab } from './LegacyProjectPromptsTab'
import PromptsWorkspace from './PromptsWorkspace'

interface ProjectPromptsTabProps {
  projectId: number
  canManage: boolean
}

/**
 * Capability-negotiated wrapper (plan Task 6): legacy-mode organizations
 * keep the existing inheritance/binding UI unchanged; owned-mode
 * organizations get the shared role-aware master/detail workspace scoped to this project. Fails
 * closed (shows an error, not the legacy UI) on a capabilities error.
 */
export function ProjectPromptsTab({ projectId, canManage }: ProjectPromptsTabProps) {
  const [mode, setMode] = useState<'legacy' | 'owned' | 'maintenance' | 'error' | null>(null)

  useEffect(() => {
    let cancelled = false
    ownedPromptsApi
      .capabilities()
      .then((caps) => {
        if (!cancelled) setMode(caps.mode)
      })
      .catch(() => {
        if (!cancelled) setMode('error')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (mode === null) {
    return (
      <p className="px-6 py-10 text-center text-sm text-harbor-text-secondary">Loading prompts…</p>
    )
  }
  if (mode === 'error') {
    return (
      <p className="px-6 py-10 text-center text-sm font-medium text-red-300">
        Failed to load the prompt library. Try reloading the page.
      </p>
    )
  }
  if (mode === 'maintenance') {
    return (
      <p className="px-6 py-10 text-center text-sm text-harbor-text-secondary">
        Prompt library migration is in progress. Try again shortly.
      </p>
    )
  }
  if (mode === 'legacy') {
    return <LegacyProjectPromptsTab projectId={projectId} canManage={canManage} />
  }
  return <PromptsWorkspace projectId={projectId} />
}
